import { baseApi } from "./baseApi";
import { API_ENDPOINTS } from "../../config/url";

// Types for user API
export interface AdminProfileData {
  _id: string;
  email: string;
  __v: number;
  countryCode: string;
  createdAt: string;
  fullName: string;
  isBlocked: boolean;
  isDeleted: boolean;
  isEmailVerified: boolean;
  isPasswordSet: boolean;
  isPhoneVerified: boolean;
  isProfileComplete: boolean;
  location: {
    coordinates: number[];
  };
  password: string;
  phone: string;
  preferences: any[];
  role: number;
  status: number;
  updatedAt: string;
  image: string | null;
  address: string | null;
  accessRole: string | null;
}

export interface AdminProfileResponse {
  data: AdminProfileData;
  message: string;
}

export interface UpdateAdminProfileRequest {
  fullName: string;
  firstName: string;
  lastName: string;
  email?: string;
  image?: string;
  phone?: string;
  countryCode?: string;
}

export interface UpdateAdminProfileResponse {
  data?: AdminProfileData;
  message?: string;
  success?: boolean;
}

// Types for Customer API
export interface CustomerItem {
  _id?: string;
  id?: string;
  email?: string;
  role?: number;
  isVerified?: boolean;
  isBlocked?: boolean;
  isProfileComplete?: boolean;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  isPasswordSet?: boolean;
  status?: number;
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  countryCode?: string;
  phone?: string;
  fullName?: string;
  image?: string;
  [key: string]: any;
}

export interface GetCustomerListRequest {
  status?: number;
  page?: number;
  limit?: number;
}

export interface GetCustomerListResponse {
  data?: {
    totalCount?: number;
    list?: CustomerItem[];
    [key: string]: any;
  };
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface DeleteCustomerRequest {
  id: string;
}

export interface DeleteCustomerResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

export interface GetCustomerDetailRequest {
  id: string;
}

export interface CustomerAddress {
  _id?: string;
  isDefault?: boolean;
  userId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  pinCode?: string;
  latitude?: number;
  longitude?: number;
  type?: number;
  isBlocked?: boolean;
  location?: {
    type?: string;
    coordinates?: number[];
  };
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export interface CustomerDetailData extends CustomerItem {
  dateOfBirth?: string;
  gender?: number;
  address?: CustomerAddress;
}

export interface GetCustomerDetailResponse {
  data?: CustomerDetailData;
  message?: string;
  success?: boolean;
  [key: string]: any;
}

export interface UpdateCustomerRequest {
  id: string;
  fullName?: string;
  email?: string;
  phone?: string;
  countryCode?: string;
  image?: string;
  dateOfBirth?: string;
  gender?: number;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    latitude?: number;
    longitude?: number;
    city?: string;
    state?: string;
    country?: string;
    pinCode?: string;
    type?: number;
    isDefault?: boolean;
  };
  status?: number;
  isBlocked?: boolean;
}

export interface UpdateCustomerResponse {
  message?: string;
  success?: boolean;
  data?: any;
  [key: string]: any;
}

// Types for Rating API
export interface RatingUser {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  countryCode: string;
  image: string;
}

export interface RatingOrder {
  _id: string;
  orderIdString?: string;
}

export interface RatingItem {
  _id: string;
  userId: string;
  orderId: string;
  ratingFor: number;
  score: number;
  reviews: string;
  createdAt: string;
  user: RatingUser;
  order: RatingOrder;
}

export interface GetRatingsRequest {
  page?: number;
  limit?: number | string;
  search?: string;
  userId?: string;
}

export interface GetRatingsResponse {
  data: {
    list: RatingItem[];
    total: number;
  };
  message: string;
}

export const userAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET - Get Admin Profile
    getAdminProfile: builder.query<AdminProfileResponse, void>({
      query: () => ({
        url: API_ENDPOINTS.ADMIN.PROFILE,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-portal": "admin",
        },
      }),
      providesTags: ["PROFILE", "USER"],
      // Cache for 5 minutes to prevent excessive API calls
      keepUnusedDataFor: 300,
    }),

    // PUT - Update Admin Profile
    updateAdminProfile: builder.mutation<UpdateAdminProfileResponse, UpdateAdminProfileRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.ADMIN.UPDATE_PROFILE,
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json",
          "x-portal": "vendor",
        },
      }),
      invalidatesTags: ["PROFILE", "USER"],
    }),

    // GET - Get Customer List
    getCustomerList: builder.query<GetCustomerListResponse, GetCustomerListRequest>({
      query: ({ status = 2, page = 1, limit = 10 }) => ({
        url: API_ENDPOINTS.CUSTOMER.LIST,
        method: "GET",
        params: {
          status,
          page,
          limit,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["USER"],
    }),

    // GET - Get Customer Detail
    getCustomerDetail: builder.query<GetCustomerDetailResponse, GetCustomerDetailRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.CUSTOMER.DETAIL(id),
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["USER"],
    }),

    // PUT - Update Customer
    updateCustomer: builder.mutation<UpdateCustomerResponse, UpdateCustomerRequest>({
      query: ({ id, ...data }) => ({
        url: API_ENDPOINTS.CUSTOMER.UPDATE(id),
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["USER"],
    }),

    // DELETE - Delete Customer
    deleteCustomer: builder.mutation<DeleteCustomerResponse, DeleteCustomerRequest>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.CUSTOMER.DELETE(id),
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["USER"],
    }),

    // GET - Get Ratings
    getRatings: builder.query<GetRatingsResponse, GetRatingsRequest>({
      query: ({ page = 1, limit = 10, search = "", userId = "" }) => {
        const params: any = { page };
        // Construct limit parameter: limit=10/USERID
        if (userId) {
          params.limit = `${limit}/${userId}`;
        } else {
          params.limit = limit;
        }

        if (search) {
          params.search = search;
        }

        return {
          url: API_ENDPOINTS.RATINGS.LIST,
          method: "GET",
          params,
          headers: {
            "Content-Type": "application/json",
            "x-portal": "admin",
          },
        };
      },
      providesTags: ["RATING"],
    }),
  }),
});

export const {
  useGetAdminProfileQuery,
  useUpdateAdminProfileMutation,
  useGetCustomerListQuery,
  useGetCustomerDetailQuery,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useGetRatingsQuery
} = userAPI;
